import { useEffect } from "react";

import { slugify, track } from "@/lib/analytics";

/**
 * `section_view {section, order}`, `section_time {section, seconds}` and
 * `vignette_view {id}` — the answer to "where exactly on the page do they stop
 * reading", which scroll depth can only approximate.
 *
 * THE DATA-ATTRIBUTE CONTRACT (this is what the copy pass writes):
 *
 *   <section data-section="platforms">   → section_view + section_time
 *   <div data-vignette="speak-a-case">   → vignette_view
 *
 * No component has to import anything or remember to call track(). Marking up
 * a band is the whole instrumentation, which is why a lane that only edits copy
 * can add measurement without touching this file.
 *
 * WHAT COUNTS AS SEEN. Half of the element is in the viewport — OR the element
 * is taller than the viewport and is filling at least half of it. Without the
 * second clause a full-height hero could never be 50% visible and would report
 * as a band nobody ever saw.
 *
 * `order` is the element's index among all `[data-section]` elements in
 * document order, read at the moment it fires. It is what turns ten counts into
 * a funnel: section 4 seen by half as many people as section 3 is the drop.
 *
 * `seconds` is whole seconds between crossing in and crossing back out, and a
 * section still on screen when the tab goes away is flushed on `pagehide` — so
 * the last band someone read is not the one band that never reports a time.
 *
 * Re-armed on route change (`page` is the effect's dependency), and a
 * MutationObserver picks up bands that arrive later: every route on this site is
 * a lazy chunk, so the sections usually do not exist yet when this first runs.
 */
export function useSectionViews(page: string): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;

    /** Ids already counted on this route — a band is one view, not one per scroll. */
    const viewed = new Set<string>();
    /** Element → the timestamp it became half-visible. */
    const enteredAt = new Map<Element, number>();
    const observed = new WeakSet<Element>();

    const idOf = (element: Element): string => {
      const raw =
        element.getAttribute("data-section") ??
        element.getAttribute("data-vignette") ??
        "";
      return slugify(raw);
    };

    const orderOf = (element: Element): number => {
      const all = Array.from(document.querySelectorAll("[data-section]"));
      const index = all.indexOf(element);
      return index < 0 ? 0 : index;
    };

    const isSection = (element: Element) =>
      element.hasAttribute("data-section");

    const leave = (element: Element, at: number) => {
      const since = enteredAt.get(element);
      if (since === undefined) return;
      enteredAt.delete(element);
      if (!isSection(element)) return;
      const seconds = Math.round((at - since) / 1000);
      track("section_time", { section: idOf(element), seconds });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        for (const entry of entries) {
          const element = entry.target;
          const half =
            entry.intersectionRatio >= 0.5 ||
            entry.intersectionRect.height >= window.innerHeight * 0.5;
          if (half) {
            if (enteredAt.has(element)) continue;
            enteredAt.set(element, now);
            const id = idOf(element);
            const key = `${isSection(element) ? "s" : "v"}:${id}`;
            if (!viewed.has(key)) {
              viewed.add(key);
              if (isSection(element)) {
                track("section_view", { section: id, order: orderOf(element) });
              } else {
                track("vignette_view", { id });
              }
            }
          } else {
            leave(element, now);
          }
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75] },
    );

    const scan = () => {
      const marked = document.querySelectorAll(
        "[data-section], [data-vignette]",
      );
      for (const element of marked) {
        if (observed.has(element)) continue;
        observed.add(element);
        observer.observe(element);
      }
    };

    let frame = 0;
    const scheduleScan = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        scan();
      });
    };

    const mutations = new MutationObserver(scheduleScan);
    mutations.observe(document.body, { childList: true, subtree: true });
    scan();

    // A section still on screen when the tab is closed or backgrounded would
    // otherwise never report a time at all — and that is the band people were
    // reading when they left, which is the one you most want to know about.
    const flush = () => {
      const now = performance.now();
      for (const element of Array.from(enteredAt.keys())) leave(element, now);
    };
    window.addEventListener("pagehide", flush);

    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
      if (frame) window.cancelAnimationFrame(frame);
      mutations.disconnect();
      observer.disconnect();
    };
  }, [page]);
}
